import React from "react";

export const ViewControls = ({
	activeTab,
	activeView,
	metricsDisplayMode,
	sortBy,
	sortOrder,
	onViewChange,
	onMetricsDisplayChange,
	onSortByChange,
	onSortOrderChange,
}) => {
	// Define sort options for each tab
	const getSortOptions = () => {
		switch (activeTab) {
			case "domains":
				return [
					{ value: "impressions", label: "Impressions" },
					{ value: "marketRequests", label: "Market Requests" },
					{ value: "compositeScore", label: "Composite Score" },
					{ value: "relevance", label: "Relevance" },
					{ value: "reach", label: "Reach" },
					{ value: "viewability", label: "Viewability" },
					{ value: "ctr", label: "CTR" },
					{ value: "vtr", label: "VTR" },
					{ value: "domain", label: "Domain Name" },
				];

			case "keywords":
				return [
					{ value: "relevance", label: "Relevance" },
					{ value: "associatedDomains", label: "Associated Domains" },
					{ value: "domain", label: "Keyword" },
				];

			case "app":
				return [
					{ value: "totalImpressions", label: "Impressions" },
					{ value: "relevance", label: "Relevance" },
					{ value: "vtr", label: "VTR" },
					{ value: "rating", label: "Rating" },
					{ value: "downloads", label: "Downloads" },
					{ value: "viewability", label: "Viewability" },
					{ value: "ctr", label: "CTR" },
					{ value: "marketRequests", label: "Market Requests" },
					{ value: "compositeScore", label: "Composite Score" },
					{ value: "name", label: "App Name" },
				];

			case "audience":
				return [
					{ value: "name", label: "Name" },
					{ value: "size", label: "Size" },
					{ value: "performance", label: "Performance" },
				];

			default:
				return [
					{ value: "relevance", label: "Relevance" },
					{ value: "name", label: "Name" },
				];
		}
	};

	const sortOptions = getSortOptions();

	// Ensure current sortBy is valid for the active tab
	React.useEffect(() => {
		const validSortValues = sortOptions.map((option) => option.value);
		if (!validSortValues.includes(sortBy)) {
			// Set to first available option if current sortBy is not valid
			onSortByChange(sortOptions[0]?.value || "relevance");
		}
	}, [activeTab, sortBy, sortOptions, onSortByChange]);

	return (
		<div className="view-controls-container">
			<div className="view-controls-content">
				{/* Left side - Sort controls */}
				<div className="sort-controls">
					<span className="sort-label">Sort by:</span>
					<select
						value={sortBy}
						onChange={(e) => onSortByChange(e.target.value)}
						className="sort-select"
					>
						{sortOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>

					<button
						onClick={() =>
							onSortOrderChange(sortOrder === "asc" ? "desc" : "asc")
						}
						className="btn btn-secondary btn-small sort-order-btn"
					>
						{sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
					</button>
				</div>
			</div>

			<style jsx>{`
        .view-controls-container {
          margin-bottom: 12px;
          background-color: var(--openx-surface);
          border-radius: 8px;
          border: 1px solid var(--openx-border);
          overflow-x: auto;
          overflow-y: visible;
        }

        .view-controls-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          min-width: 600px;
          gap: 16px;
        }

        .sort-controls {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-shrink: 0;
        }

        .sort-label {
          fontSize: 14px;
          fontWeight: 500;
          color: var(--openx-text-primary);
          white-space: nowrap;
        }

        .sort-select {
          padding: 6px 12px;
          border: 1px solid var(--openx-border);
          border-radius: 4px;
          fontSize: 14px;
          background-color: var(--openx-surface);
          color: var(--openx-text-primary);
          min-width: 120px;
          white-space: nowrap;
        }

        .sort-select:focus {
          outline: none;
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .sort-order-btn {
          white-space: nowrap;
          min-width: 100px;
        }

        .display-controls {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        }

        .display-label {
          fontSize: 13px;
          color: var(--openx-text-secondary);
          white-space: nowrap;
        }

        .display-buttons {
          display: flex;
          gap: 8px;
        }

        .btn {
          padding: 6px 12px;
          border: 1px solid var(--openx-border);
          border-radius: 4px;
          fontSize: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .btn-small {
          padding: 4px 8px;
          fontSize: 12px;
        }

        .btn-primary {
          background-color: #0076BD;
          color: white;
          border-color: #0076BD;
        }

        .btn-primary:hover {
          background-color: #005a94;
          border-color: #005a94;
        }

        .btn-secondary {
          background-color: var(--openx-surface);
          color: var(--openx-text-primary);
          border-color: var(--openx-border);
        }

        .btn-secondary:hover {
          background-color: var(--openx-surface-hover, #f8f9fa);
          border-color: var(--openx-border-hover, #adb5bd);
        }

        /* Tablet styles */
        @media (max-width: 768px) {
          .view-controls-content {
            min-width: 500px;
            padding: 12px 16px;
            gap: 12px;
          }

          .sort-controls {
            gap: 8px;
          }

          .sort-label,
          .display-label {
            fontSize: 12px;
          }

          .sort-select {
            fontSize: 12px;
            padding: 4px 8px;
            min-width: 100px;
          }

          .btn {
            fontSize: 12px;
            padding: 4px 8px;
          }

          .btn-small {
            padding: 3px 6px;
            fontSize: 11px;
          }

          .sort-order-btn {
            min-width: 80px;
          }
        }

        /* Mobile styles */
        @media (max-width: 480px) {
          .view-controls-content {
            min-width: 400px;
            padding: 10px 12px;
            gap: 10px;
            flex-direction: column;
            align-items: stretch;
          }

          .sort-controls,
          .display-controls {
            justify-content: center;
            flex-wrap: wrap;
          }

          .sort-controls {
            gap: 6px;
          }

          .display-controls {
            gap: 6px;
          }

          .sort-label,
          .display-label {
            fontSize: 11px;
            min-width: auto;
          }

          .sort-select {
            fontSize: 11px;
            padding: 3px 6px;
            min-width: 90px;
            flex: 1;
            max-width: 120px;
          }

          .btn {
            fontSize: 11px;
            padding: 3px 6px;
          }

          .btn-small {
            padding: 2px 4px;
            fontSize: 10px;
          }

          .sort-order-btn {
            min-width: 70px;
            flex: 1;
            max-width: 100px;
          }

          .display-buttons {
            gap: 4px;
            flex: 1;
          }

          .display-buttons .btn {
            flex: 1;
          }
        }

        /* Very small screens */
        @media (max-width: 320px) {
          .view-controls-content {
            min-width: 300px;
            padding: 8px 10px;
          }

          .sort-select {
            min-width: 80px;
          }

          .sort-order-btn {
            min-width: 60px;
          }
        }

        /* Custom scrollbar for webkit browsers */
        .view-controls-container::-webkit-scrollbar {
          height: 6px;
        }

        .view-controls-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .view-controls-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .view-controls-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
		</div>
	);
};
